import { useCallback, useState } from 'react';
import { showToastrMessage } from '@/utilities/Toastr.Utils';
import type {
  ISuggestedAddressError,
  ISuggestedAddressErrorWithData,
} from '@/common/interfaces/error.interface';
import { getSuggestedAddress } from '@/utilities/CreateOrderUtils';
import { UPS_VALIDATION_SUGGEST_PARTIAL_TEXT } from '@/constants/Order';
import type { ShippingInformation } from '@/common/interfaces/groupOrder.interface';
import { useValidateAddressMutation } from '@/api/OrderQuery';
import { useAppDispatch } from '@/store/hooks';
import { setAddressValidationCount } from '@/store/reducers/order';

interface Address {
  shippingName: string;
  street1: string;
  street2: string;
  city: string;
  state: string;
  zipCode: string;
}

/**
 * Hook to validate address.
 */
const useAddressValidation = () => {
  const [suggestions, setSuggestions] = useState<Address[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [validateAddress] = useValidateAddressMutation();
  const dispatch = useAppDispatch();

  const validateAddressInfo = useCallback(
    async (formValues: ShippingInformation) => {
      const address = {
        shippingName: formValues.shippingName,
        street1: formValues.street1,
        street2: formValues.street2,
        city: formValues.city,
        state: typeof formValues.state === 'string' ? formValues.state : formValues.state.value,
        zipCode: formValues.shippingZipCode,
      };

      setSuggestions([]);
      setShowSuggestions(false);

      /**
       * Increment the address validation count to trigger the address validation
       */
      dispatch(setAddressValidationCount());

      let validateAddressRes = await validateAddress(address);

      validateAddressRes =
        (validateAddressRes as ISuggestedAddressErrorWithData).data || validateAddressRes;

      if ('error' in validateAddressRes) {
        const errorRes = validateAddressRes as ISuggestedAddressError;

        if (errorRes.error.data.suggestions) {
          const suggestionRecords = getSuggestedAddress(
            errorRes.error.data.suggestions,
            formValues.shippingName
          );

          setSuggestions(suggestionRecords);
          setShowSuggestions(true);
          showToastrMessage(UPS_VALIDATION_SUGGEST_PARTIAL_TEXT, 'failure');
        } else {
          showToastrMessage(UPS_VALIDATION_SUGGEST_PARTIAL_TEXT, 'failure');
          setShowSuggestions(false);
        }

        return false;
      }

      setShowSuggestions(false);
      return true;
    },
    [dispatch, validateAddress]
  );

  const clearAddressSuggestions = useCallback(() => {
    setSuggestions([]);
    setShowSuggestions(false);
  }, []);

  return {
    suggestions,
    showSuggestions,
    validateAddressInfo,
    clearAddressSuggestions,
  };
};

export default useAddressValidation;
